import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExternalLinkAlt, faArrowLeft } from "@fortawesome/free-solid-svg-icons";

import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";

import { useTheme } from "../contexts/ThemeContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useArticles } from "../hooks/useArticles";
import { useUserInfo } from "../hooks/useUserInfo";
import { Theme } from "../theme";

// アニメーション
const fadeInUp = keyframes`
	from {
		opacity: 0;
		transform: translateY(20px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
`;

// Styled Components
const PageContainer = styled.div<{ theme: Theme }>`
	min-height: 100vh;
	background: ${props => props.theme.colors.background.primary};
	transition: background-color 0.3s ease;
`;

const ContentWrapper = styled.div`
	max-width: 900px;
	margin: 0 auto;
	padding: 0 2rem;

	@media (max-width: 768px) {
		padding: 0 1rem;
	}
`;

const ArticleContainer = styled.div`
	padding-top: 3rem;
	animation: ${fadeInUp} 0.6s ease-out;
`;

const BackButton = styled.button<{ theme: Theme }>`
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.75rem 1.5rem;
	background: ${props => props.theme.colors.background.secondary};
	border: 1px solid ${props => props.theme.colors.neutral[200]};
	border-radius: 0.75rem;
	color: ${props => props.theme.colors.text.primary};
	font-weight: 600;
	font-size: 0.95rem;
	cursor: pointer;
	transition: all 0.2s ease;
	margin-bottom: 2rem;

	&:hover {
		background: ${props => props.theme.mode === 'light' ? '#f0f4ff' : props.theme.colors.neutral[100]};
		border-color: ${props => props.theme.colors.primary.main};
		color: ${props => props.theme.colors.primary.main};
		transform: translateX(-4px);
	}

	svg {
		transition: transform 0.2s ease;
	}

	&:hover svg {
		transform: translateX(-2px);
	}
`;

const DateContainer = styled.div<{ theme: Theme }>`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	margin-bottom: 2rem;

	&::before {
		content: '';
		width: 3px;
		height: 20px;
		background: ${props => props.theme.colors.primary.main};
		border-radius: 2px;
	}
`;

const Date = styled.div<{ theme: Theme }>`
	color: ${props => props.theme.colors.text.tertiary};
	font-size: 0.95rem;
	font-weight: 500;
`;

const Title = styled.h1<{ theme: Theme }>`
	font-size: ${props => props.theme.typography.roles.pageHeading.fontSize};
	font-weight: ${props => props.theme.typography.roles.pageHeading.fontWeight};
	color: ${props => props.theme.colors.text.primary};
	line-height: ${props => props.theme.typography.roles.pageHeading.lineHeight};
	letter-spacing: ${props => props.theme.typography.roles.pageHeading.letterSpacing};
	margin: 0 0 2rem 0;

	@media (max-width: 768px) {
		letter-spacing: -0.015em;
	}
`;

const ArticleBody = styled.div<{ theme: Theme }>`
	color: ${props => props.theme.colors.text.secondary};
	font-size: 1.125rem;
	line-height: 1.8;
	margin-bottom: 2rem;
`;

const ExternalLinkCard = styled.div<{ theme: Theme }>`
	background: ${props => props.theme.mode === 'light' ? '#f0f4ff' : props.theme.colors.neutral[100]};
	border: 2px solid ${props => props.theme.colors.primary.main};
	border-radius: 1.5rem;
	padding: 3rem;
	text-align: center;
	margin: 2rem 0;
	animation: ${fadeInUp} 0.6s ease-out 0.2s both;

	p {
		color: ${props => props.theme.colors.text.secondary};
		font-size: 1.125rem;
		margin-bottom: 2rem;
		line-height: 1.8;
	}

	a {
		display: inline-flex;
		align-items: center;
		gap: 0.75rem;
		padding: 1rem 2rem;
		background: ${props => props.theme.colors.primary.gradient};
		color: white;
		text-decoration: none;
		border-radius: 0.75rem;
		font-weight: 600;
		font-size: 1.125rem;
		transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
		box-shadow: ${props => props.theme.shadows.md};

		&:hover {
			box-shadow: ${props => props.theme.shadows.glowHover};
			transform: translateY(-2px);
		}

		svg {
			font-size: 1rem;
		}
	}
`;

const FooterWrapper = styled.div`
	margin-top: 6rem;
`;

let ArticleStyle = styled.div``;

const ReadArticle = () => {
	const navigate = useNavigate();
	const { slug } = useParams();
	const { theme } = useTheme();
	const { t } = useLanguage();
	const articles = useArticles();
	const userInfo = useUserInfo();

	const article = articles[Number(slug) - 1];

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [article]);

	ArticleStyle = styled.div`
		${article.style}
	`;

	return (
		<React.Fragment>
			<Helmet>
				<title>{`${article.title} | ${userInfo.main.title}`}</title>
				<meta name="description" content={article.description} />
				<meta name="keywords" content={article.keywords.join(", ")} />
			</Helmet>

			<PageContainer theme={theme}>
				<NavBar />
				<ContentWrapper>
					<ArticleContainer>
						<BackButton theme={theme} onClick={() => navigate(-1)}>
							<FontAwesomeIcon icon={faArrowLeft} />
							{t.outputs.backToOutputs}
						</BackButton>

						<DateContainer theme={theme}>
							<Date theme={theme}>{article.date}</Date>
						</DateContainer>

						<Title theme={theme}>{article.title}</Title>

						<div>
							{article.body ? (
								<ArticleStyle>{article.body}</ArticleStyle>
							) : (
								<>
									<ArticleBody theme={theme}>
										{article.description}
									</ArticleBody>
									<ExternalLinkCard theme={theme}>
										<p>{t.outputs.externalNotice}</p>
										<a href={article.link} target="_blank" rel="noopener noreferrer">
											{t.outputs.readArticle}
											<FontAwesomeIcon icon={faExternalLinkAlt} />
										</a>
									</ExternalLinkCard>
								</>
							)}
						</div>
					</ArticleContainer>
					<FooterWrapper>
						<Footer />
					</FooterWrapper>
				</ContentWrapper>
			</PageContainer>
		</React.Fragment>
	);
};

export default ReadArticle;
